/**
 * @file index.tsx
 *
 * @fileoverview Exports job detail utils functions.
 */

import Link from 'next/link';
import Router from 'next/router';
import { ReactNode, useContext } from 'react';
import { FormattedMessage, IntlShape } from 'react-intl';
import { getOrgProfileFromJob } from '~/jobiqo/components/Job/utils';
import { getTeaserImage } from '~/jobiqo/components/Job/utils/logo';
import JobHeaderExtraInfoSlot from '~/jobiqo/components/Slots/JobHeaderExtraInfoSlot';
import { PathContext } from '~/jobiqo/context/app';
import { PathContextInterface } from '~/jobiqo/models/app';
import {
  getJobFieldLabels,
  getJobLocationToRender,
  getJobDatesToRender,
  isJobIframe,
} from '~/jobiqo/services/jobs';
import { generateQueryStringURL } from '~/jobiqo/services/links';
import { Job, Term, WorkflowState } from '~/jobiqo/types/types';
import JobHeaderCustomBadgeSlot from '~/jobiqo/components/Slots/JobHeaderCustomBadgeSlot';
import { JobTopJobIconSlot } from '~/jobiqo/components/Slots/JobTopJobIconSlot';
import { JobHighlightJobIconSlot } from '~/jobiqo/components/Slots/JobHighlightJobIconSlot';
import { OrganizationPromotedIconSlot } from '~/jobiqo/components/Slots/OrganizationPromotedIconSlot';
import { OrganizationIconSlot } from '~/jobiqo/components/Slots/OrganizationIconSlot';
import { getCurrentFrontendConfig } from '~/jobiqo/context/frontendConfig';
export { getProcessedTrackingPixelCode } from './trackingPixel';

const EMPLOYMENT_TYPE_FILTER = 'field_job_employment_type';
const ORGANIZATION_NAME_FILTER = 'job_organization_name';

/**
 * Builds a job search URL with the given query parameters.
 *
 * @param jobsPage
 *   The configured jobs search page path.
 * @param query
 *   Query parameters to append to the jobs search page.
 *
 * @returns
 *   The jobs search URL with encoded query parameters.
 */
function getJobSearchUrl(
  jobsPage: string,
  query: Record<string, string | number>,
): string {
  return `${jobsPage}?${generateQueryStringURL(query)}`;
}

/**
 * Builds a job search clean URL from a taxonomy URL alias.
 *
 * @param jobsPage
 *   The configured jobs search page path.
 * @param urlAlias
 *   The taxonomy term URL alias to append to the jobs search page.
 *
 * @returns
 *   The jobs search clean URL for the given alias.
 */
function getJobSearchAliasUrl(jobsPage: string, urlAlias: string): string {
  return `${jobsPage}/${urlAlias.replace(/^\//, '')}`;
}

/**
 * Builds the organization label displayed in the job detail header.
 *
 * Links to the published organization profile when available. If the job has
 * no published organization profile URL, it falls back to the jobs SERP
 * filtered by organization name.
 *
 * @param job
 *   The job whose organization should be rendered.
 * @param orgProfile
 *   The normalized organization display data extracted from the job.
 * @param jobsPage
 *   The configured jobs search page path.
 *
 * @returns
 *   The linked organization label, or plain text when no label is available.
 */
function getJobDetailCompany(
  job: Job,
  orgProfile: ReturnType<typeof getOrgProfileFromJob>,
  jobsPage: string,
): ReactNode {
  const organizationProfileUrl =
    job.absoluteOrgUrl ??
    job.organizationProfile?.urlNoPrefix ??
    job.organizationProfile?.url?.path;

  if (organizationProfileUrl && orgProfile.isPublished) {
    return (
      <Link
        href={organizationProfileUrl}
        {...(job.absoluteOrgUrl && { target: '_blank' })}
      >
        {orgProfile.label}
      </Link>
    );
  }

  return orgProfile.label ? (
    <Link
      href={getJobSearchUrl(jobsPage, {
        [ORGANIZATION_NAME_FILTER]: orgProfile.label,
      })}
    >
      {orgProfile.label}
    </Link>
  ) : (
    orgProfile.label
  );
}

/**
 * Builds job location values for the job detail header.
 *
 * The displayed location label still comes from getJobLocationToRender(), so
 * configured location hiding and formatting stay unchanged. When the matching
 * job region term has a URL alias, the label links to that location's jobs SERP
 * clean URL; otherwise the location remains plain text.
 *
 * @param job
 *   The job whose locations should be rendered.
 * @param jobsPage
 *   The configured jobs search page path.
 *
 * @returns
 *   Location labels for the job detail header, linked when a region alias is
 *   available.
 */
function getJobLocationHeaderItems(job: Job, jobsPage: string): ReactNode[] {
  return getJobLocationToRender(job).map((location, index) => {
    const region = job.region?.[index];
    const urlAlias = region?.urlAlias;

    return location && urlAlias ? (
      <Link
        key={`${location}-${urlAlias}`}
        href={getJobSearchAliasUrl(jobsPage, urlAlias)}
      >
        {location}
      </Link>
    ) : (
      location
    );
  });
}

/**
 * Builds linkable taxonomy term values for the job detail header.
 *
 * Used for employment type links so each value points to the jobs SERP.
 *
 * When a term has a URL alias, it uses the clean URL path. If no alias is
 * available, it falls back to the facet query filter URL.
 *
 * @param terms
 *   Taxonomy terms to render as links.
 * @param jobsPage
 *   The configured jobs search page path.
 *
 * @returns
 *   Linkable term labels for the job detail header.
 */
function getLinkedJobFieldTerms(
  terms: Job['employmentType'],
  jobsPage: string,
): ReactNode[] {
  return (terms ?? [])
    .filter((term): term is Term => Boolean(term?.label))
    .map((term) => {
      const href = term.urlAlias
        ? getJobSearchAliasUrl(jobsPage, term.urlAlias)
        : getJobSearchUrl(jobsPage, {
            [EMPLOYMENT_TYPE_FILTER]: term.tid,
          });

      return (
        <Link key={term.tid} href={href}>
          {term.label}
        </Link>
      );
    });
}

/**
 *  Extracts common JobDetail component props from the given job.
 *  Used in JobDetailView and BookmarkedJobsDetailPanel.
 *
 * @param {Job} job
 *  The job.
 * @param {IntlShape} intl
 *  The intl object.
 * @returns
 *  JobDetail common props.
 */
export function extractJobDetailCommonProps(job: Job, intl: IntlShape) {
  const frontendConfig = getCurrentFrontendConfig();
  const logo = getTeaserImage(job);
  const isIframe = isJobIframe(job);
  const orgProfile = getOrgProfileFromJob(job);
  const jobsPage = frontendConfig.JOBS.JOBS_PAGE;
  const salaryRangeFreeField =
    'salaryRangeFree' in job ? job.salaryRangeFree : undefined;
  const salaryRangeFreeOutput = getFormattedSalaryRangeOutput(
    salaryRangeFreeField ?? {},
    intl,
  );

  const jobFieldsValues: Array<ReactNode[]> = [];
  // Check if job properties are defined.
  if (job.occupationalField && job.occupationalField.length > 0) {
    jobFieldsValues.push(getJobFieldLabels(job.occupationalField));
  }
  if (job.employmentType && job.employmentType.length > 0) {
    const linkedEmploymentTypes = getLinkedJobFieldTerms(
      job.employmentType,
      jobsPage,
    );
    if (linkedEmploymentTypes.length > 0) {
      jobFieldsValues.push(linkedEmploymentTypes);
    }
  }
  if (job.yearsOfExperience && job.yearsOfExperience.length > 0) {
    jobFieldsValues.push(getJobFieldLabels(job.yearsOfExperience));
  }
  if (job.salaryRange?.[0]?.label) {
    jobFieldsValues.push([job.salaryRange[0].label]);
  }
  if (salaryRangeFreeOutput) {
    jobFieldsValues.push([salaryRangeFreeOutput]);
  }

  return {
    onShrinkHeaderBack: () => Router.back(),
    title: job.title,
    dates: getJobDatesToRender(job, intl),
    company: getJobDetailCompany(job, orgProfile, jobsPage),
    ...(logo && {
      picture: {
        url: logo,
        alt: job.title,
        title: job.title,
      },
    }),
    featured:
      frontendConfig.JOBS.DETAIL_SHOW_FEATURED_INDICATORS &&
      job.isHighlight &&
      !job.isTopJob,
    topJob: frontendConfig.JOBS.DETAIL_SHOW_TOP_JOB_INDICATORS && job.isTopJob,
    topJobText: <FormattedMessage id="top_job" defaultMessage="Top job" />,
    topJobIcon: <JobTopJobIconSlot job={job} />,
    premiumCompany:
      frontendConfig.ORGANIZATIONS.SHOW_PREMIUM_INDICATIONS &&
      job.organizationProfile?.['isPremium'],
    featuredtext: (
      <FormattedMessage id="job_featured" defaultMessage="Featured job" />
    ),
    featuredJobIcon: <JobHighlightJobIconSlot job={job} />,
    organizationIcon: <OrganizationIconSlot />,
    promotedOrganizationIcon: <OrganizationPromotedIconSlot />,
    ...(job.remoteOptions &&
      job.remoteOptions?.length > 0 && {
        remote: job?.remoteOptions.map((option) => option?.label).join(' | '),
      }),
    isIframe: isIframe,
    locations: getJobLocationHeaderItems(job, jobsPage),
    jobFieldsValues,
    jobHeaderExtraInfo: <JobHeaderExtraInfoSlot job={job} />,
    customBadge: <JobHeaderCustomBadgeSlot job={job} />,
    sourcePortals: job.sourcePortals as string[],
  };
}

/**
 * Checks current pathname and determines if the current page
 * is a job search page.
 *
 * @returns {boolean}
 *  True if the current page is a job search page.
 */
export const isCurrentPageJobSearch = (): boolean => {
  const { originalPath }: PathContextInterface = useContext(PathContext);
  return originalPath?.startsWith('/jobs') ?? false;
};

/**
 * Checks if the job is unpublished.
 * @param job
 *  The job.
 * @returns
 *  True if the job is unpublished.
 */
export function getIsJobUnpublished(job: Job) {
  return job.workflowState.value === 'unpublished';
}

/**
 * Checks if the job is paused.
 * @param job
 *  The job.
 * @returns
 *  True if the job is paused.
 */
export function getIsJobPaused(job: Job) {
  return job.workflowState.value === 'paused';
}

/**
 * Checks if the job is published.
 * @param job
 *  The job.
 * @returns
 *  True if the job is published.
 */
export function getIsJobPublished(job: Job) {
  return getIsJobWorkflowPublished(job.workflowState);
}

/**
 * If the workflowState is defined and has a value of 'published', return true,
 * otherwise return false.
 * @param {WorkflowState} workflowState
 *  The workflow state of the job.
 * @returns
 *  True if the workflowState is defined and has a value of 'published',
 * otherwise return false.
 */
export function getIsJobWorkflowPublished(workflowState?: WorkflowState) {
  return workflowState?.value === 'published';
}

/**
 * If the workflowState is defined and has a value of 'paused', return true,
 * otherwise return false.
 * @param {WorkflowState} workflowState
 *  The workflow state of the job.
 * @returns
 *  True if the workflowState is defined and has a value of 'paused',
 * otherwise return false.
 */
export function getIsJobWorkflowPaused(workflowState?: WorkflowState) {
  return workflowState?.value === 'paused';
}

/**
 * If the workflowState is defined and has a value of 'in_progress' or 'needs_review' (job is posted but not yet published),
 * return true, otherwise return false.
 *
 * @param {WorkflowState} workflowState
 *  The workflow state of the job.
 *
 * @returns
 *  True if the workflowState is defined and has a value of 'in_progress' or 'needs_review',
 * otherwise return false.
 */
export function getIsJobInPendingState(workflowState?: WorkflowState) {
  return (
    workflowState?.value === 'in_progress' ||
    workflowState?.value === 'needs_review'
  );
}

/**
 * Get custom params for job detail iFrame. For example CMP specific params.
 *
 * @returns {Object | null}
 *   The object with params or null.
 */
export function getIframeCustomParams(): object | null {
  switch (getCurrentFrontendConfig().APP.CMP_PROVIDER) {
    case 'ConsentManager':
      return {
        'data-cmp-ab': '1',
      };

    default:
      return null;
  }
}

/**
 * Helper function to format Salary range field value to display on the frontend. If we have both values
 * we use dash between values, otherwise we prefix single value.
 *
 * @param salaryRangeFree
 *  The values from Job Salary range free input field.
 * @param intl
 *  The intl object.
 *
 * @returns {string}
 *  The formatted salary range value.
 */
export function getFormattedSalaryRange(
  salaryRangeFree: any,
  intl: IntlShape,
): string {
  let formattedSalaryRange = '';

  // Prepare min salary and max salary prefixes for the case when there is only one of the values.
  const minSalaryPrefix = intl.formatMessage({
    id: 'job_salary_min_value_prefix',
    defaultMessage: 'From',
  });
  const maxSalaryPrefix = intl.formatMessage({
    id: 'job_salary_max_value_prefix',
    defaultMessage: 'Up to',
  });

  const formattedMinSalary = getFormattedSalaryValue(
    salaryRangeFree?.minSalary ?? '',
    salaryRangeFree?.currencyCode ?? '',
    intl,
  );
  const formattedMaxSalary = getFormattedSalaryValue(
    salaryRangeFree?.maxSalary ?? '',
    salaryRangeFree?.currencyCode ?? '',
    intl,
  );

  if (formattedMinSalary && formattedMaxSalary) {
    formattedSalaryRange =
      salaryRangeFree?.minSalary == salaryRangeFree?.maxSalary
        ? formattedMinSalary
        : `${formattedMinSalary} - ${formattedMaxSalary}`;
  } else if (formattedMinSalary) {
    formattedSalaryRange = `${minSalaryPrefix} ${formattedMinSalary}`;
  } else if (formattedMaxSalary) {
    formattedSalaryRange = `${maxSalaryPrefix} ${formattedMaxSalary}`;
  }

  return formattedSalaryRange;
}

/**
 * Helper function to get salary in the price format based on number and currency.
 *
 * @param {string} salaryValue
 *  The salary value.
 * @param {string} salaryCurrency
 *  The salary currency represented by currency code.
 * @param {IntlShape} intl
 *  The intl object.
 *
 * @returns {string}
 *  The correctly formatted price representation of salary.
 */
export function getFormattedSalaryValue(
  salaryValue: string,
  salaryCurrency: string,
  intl: IntlShape,
): string {
  // 0/"0"/null/"" are treated as "no value".
  return parseFloat(salaryValue) > 0 && salaryCurrency
    ? intl.formatNumber(parseFloat(salaryValue), {
        style: 'currency',
        currency: salaryCurrency,
        trailingZeroDisplay: 'stripIfInteger',
        maximumFractionDigits: 2,
      })
    : '';
}

/**
 * Get locale decimal value.
 *
 * @param intl
 *  The intl object.
 * @param value
 *  The value to format.
 *
 * @returns
 *  The formatted decimal value.
 */
export function getLocaleDecimalValue(intl: IntlShape, value: number): string {
  return intl.formatNumber(value, {
    style: 'decimal',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: true,
  });
}

/**
 * Get human-readable formatted salary unit based on the structured data unitText value.
 *
 * @param {string} salaryUnit
 *  The structured data unitText value.
 * @param {IntlShape} intl
 *  The intl object.
 *
 * @returns {string}
 *  The formatted salary unit value.
 */
export function getSalaryUnitMessage(
  salaryUnit: string,
  intl: IntlShape,
): string {
  switch (salaryUnit) {
    case 'HOUR':
      return intl.formatMessage({
        id: 'job_salary_unit_hour',
        defaultMessage: 'per hour',
      });
    case 'DAY':
      return intl.formatMessage({
        id: 'job_salary_unit_day',
        defaultMessage: 'per day',
      });
    case 'WEEK':
      return intl.formatMessage({
        id: 'job_salary_unit_week',
        defaultMessage: 'per week',
      });
    case 'MONTH':
      return intl.formatMessage({
        id: 'job_salary_unit_month',
        defaultMessage: 'per month',
      });
    case 'YEAR':
      return intl.formatMessage({
        id: 'job_salary_unit_year',
        defaultMessage: 'per year',
      });
    default:
      return '';
  }
}

/**
 * Get formatted salary range output.
 *
 * @param salaryRangeFreeField
 *  The values from Job Salary range free input field.
 * @param intl
 *  The intl object.
 *
 * @returns {string}
 *  The formatted salary range output.
 */
export function getFormattedSalaryRangeOutput(
  salaryRangeFreeField: any,
  intl: IntlShape,
) {
  const formattedSalaryRange = getFormattedSalaryRange(
    salaryRangeFreeField,
    intl,
  );
  const salaryUnitMessage = getSalaryUnitMessage(
    salaryRangeFreeField?.salaryUnit ?? '',
    intl,
  );
  return `${formattedSalaryRange} ${salaryUnitMessage}`.trim();
}
